import { create } from 'zustand';
import type {
  ErrorCode,
  PublicRoomSummary,
  RejoinOption,
  RejoinRequest,
  RejoinStatus,
  RoomView,
} from '@bm/shared';

/**
 * `outdated` und `takenOver` sind Endzustände: Der Client versucht dann nicht
 * weiter zu verbinden, weil weitere Versuche nichts ändern würden.
 */
export type ConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'offline'
  | 'outdated'
  | 'takenOver';

/**
 * Entweder ein Fehlercode — die Übersetzung bleibt dann beim Client — oder ein
 * fertiger Text für Hinweise, die kein Protokollfehler sind.
 */
export type ToastEntry = { id: number } & ({ code: ErrorCode } | { text: string });

/** Stand der eigenen Wiedereinstiegs-Anfrage. */
export interface RejoinState {
  requestId: string;
  status: RejoinStatus;
}

interface AppState {
  connectionStatus: ConnectionStatus;
  /** `serverNow - Date.now()` zum Messzeitpunkt — Basis der Timer-Berechnung. */
  clockOffsetMs: number;
  /** Wann der nächste Verbindungsversuch startet; `null` heißt „kein Versuch geplant". */
  nextRetryAt: number | null;
  /** Abgeschickte, aber noch nicht bestätigte Aktionen. */
  pendingActions: number;
  sessionToken: string | null;
  playerId: string | null;

  lobbyRooms: PublicRoomSummary[];
  view: RoomView | null;
  toasts: ToastEntry[];

  /** Verwaiste Plätze, die auf den gesuchten Namen passen. */
  rejoinOptions: RejoinOption[];
  rejoin: RejoinState | null;
  /** Fremde Anfragen, über die dieser Spieler entscheiden darf. */
  rejoinRequests: RejoinRequest[];

  soundEnabled: boolean;

  setConnectionStatus: (status: ConnectionStatus) => void;
  setClockOffset: (offsetMs: number) => void;
  setNextRetryAt: (at: number | null) => void;
  setPendingActions: (count: number) => void;
  setSession: (session: { token: string; playerId: string }) => void;
  setLobbyRooms: (rooms: PublicRoomSummary[]) => void;
  setView: (view: RoomView) => void;
  clearView: () => void;
  setRejoinOptions: (options: RejoinOption[]) => void;
  setRejoin: (rejoin: RejoinState | null) => void;
  setRejoinRequests: (requests: RejoinRequest[]) => void;
  pushError: (code: ErrorCode) => void;
  pushNotice: (text: string) => void;
  dismissToast: (id: number) => void;
  setSoundEnabled: (enabled: boolean) => void;
}

let nextToastId = 1;

const SOUND_STORAGE_KEY = 'bm.soundEnabled';

function loadSoundPreference(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(SOUND_STORAGE_KEY) === 'true';
}

export const useAppStore = create<AppState>((set) => ({
  connectionStatus: 'connecting',
  clockOffsetMs: 0,
  nextRetryAt: null,
  pendingActions: 0,
  sessionToken: null,
  playerId: null,

  lobbyRooms: [],
  view: null,
  toasts: [],

  rejoinOptions: [],
  rejoin: null,
  rejoinRequests: [],

  soundEnabled: loadSoundPreference(),

  setConnectionStatus: (status) => set({ connectionStatus: status }),
  setClockOffset: (offsetMs) => set({ clockOffsetMs: offsetMs }),
  setNextRetryAt: (at) => set({ nextRetryAt: at }),
  setPendingActions: (count) => set({ pendingActions: count }),
  setSession: ({ token, playerId }) => set({ sessionToken: token, playerId }),
  setLobbyRooms: (rooms) => set({ lobbyRooms: rooms }),
  setView: (view) => set({ view }),
  // Mit dem Raum verschwinden auch die Anfragen, über die man dort entschieden hätte.
  clearView: () => set({ view: null, rejoinRequests: [] }),
  setRejoinOptions: (options) => set({ rejoinOptions: options }),
  setRejoin: (rejoin) => set({ rejoin }),
  setRejoinRequests: (requests) => set({ rejoinRequests: requests }),
  pushError: (code) =>
    set((state) => ({ toasts: [...state.toasts, { id: nextToastId++, code }] })),
  pushNotice: (text) =>
    set((state) => ({ toasts: [...state.toasts, { id: nextToastId++, text }] })),
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  setSoundEnabled: (enabled) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SOUND_STORAGE_KEY, String(enabled));
    }
    set({ soundEnabled: enabled });
  },
}));

/** Serverzeit jetzt, aus dem zuletzt gemessenen Offset abgeleitet (GDD §28). */
export function serverNow(): number {
  return Date.now() + useAppStore.getState().clockOffsetMs;
}
